import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { AuthContext } from '@/context/auth-context';
import { useLogout } from '@/hooks/use-logout';
import { LAST_ACTIVITY_KEY, LOGOUT_SIGNAL_KEY } from '@/constants/session-keys';
import { LogoutReason, readLogoutReason } from '@/utils/logout-broadcast';

const WARNING_MS = 60_000;
const WARNING_SECONDS = Math.floor(WARNING_MS / 1000);
const ACTIVITY_THROTTLE_MS = 1_000;
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

// The value is baked in at build time, so one report carries all the signal
// there is. This runs on every layout render — reporting each time would flood
// Sentry with the same misconfiguration.
let reportedInvalidTimeout: string | null = null;

// Opt-in per deployment: with no VITE_IDLE_TIMEOUT_MINUTES set, idle logout is
// off. Only environments that must meet the inactivity requirement enable it.
function resolveTimeoutMs(): number | null {
  const raw = import.meta.env.VITE_IDLE_TIMEOUT_MINUTES;
  if (raw === undefined || String(raw).trim() === '') {
    return null;
  }

  const minutes = Number(raw);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    // Set but unusable ('60m', a stray space). Silently disabling would leave
    // an environment that asked for a timeout with none and no way to notice.
    const value = String(raw);
    if (reportedInvalidTimeout !== value) {
      reportedInvalidTimeout = value;
      Sentry.captureException(
        new Error(`Invalid VITE_IDLE_TIMEOUT_MINUTES: ${value}`),
        { extra: { context: 'resolveTimeoutMs' } },
      );
    }
    return null;
  }

  return minutes * 60_000;
}

// Stamped with the user so a timestamp left behind by a previous session can't
// log the next person out the moment they sign in.
function parseLastActivity(raw: string | null, userId: string): number | null {
  try {
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.u !== userId) return null;
    const at = Number(parsed.t);
    return Number.isFinite(at) && at > 0 ? at : null;
  } catch {
    return null;
  }
}

function readLastActivity(userId: string): number | null {
  return parseLastActivity(localStorage.getItem(LAST_ACTIVITY_KEY), userId);
}

function writeLastActivity(userId: string, at: number) {
  try {
    localStorage.setItem(
      LAST_ACTIVITY_KEY,
      JSON.stringify({ t: at, u: userId }),
    );
  } catch {
    // ignore storage failures — the local timer is still authoritative here
  }
}

export function isIdleTimeoutEnabled(): boolean {
  return resolveTimeoutMs() !== null;
}

export function useIdleTimeout() {
  const { user } = useContext(AuthContext);
  const logout = useLogout();
  const userId = user?.id ?? '';
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const showWarningRef = useRef(false);
  const haltedRef = useRef(false);
  const lastWriteRef = useRef(0);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const timeoutMsRef = useRef(resolveTimeoutMs());

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  // Halt first: the click that triggered this bubbles to the window activity
  // listener, which would otherwise write fresh activity and tell other tabs
  // to stay alive right after we told them to leave.
  const triggerLogout = useCallback(
    ({
      broadcast = true,
      reason = 'idle',
    }: { broadcast?: boolean; reason?: LogoutReason } = {}) => {
      haltedRef.current = true;
      clearTimers();
      showWarningRef.current = false;
      setShowWarning(false);

      logoutRef.current({ broadcast, reason });
    },
    [clearTimers],
  );

  const schedule = useCallback(() => {
    clearTimers();
    const timeoutMs = timeoutMsRef.current;
    if (timeoutMs === null || haltedRef.current) {
      return;
    }

    // Seeded from the shared timestamp, so the window survives a reload and is
    // one clock across tabs rather than a fresh one per mount.
    const lastActivity = readLastActivity(userIdRef.current);
    const elapsed = lastActivity === null ? 0 : Date.now() - lastActivity;
    const remaining = timeoutMs - Math.max(0, elapsed);

    if (remaining <= 0) {
      triggerLogout();
      return;
    }

    // A timeout shorter than the warning lead gets a proportionally shorter
    // countdown instead of one that claims more time than it has.
    const warningWindowMs = Math.min(WARNING_MS, remaining);
    const warningSeconds = Math.max(1, Math.round(warningWindowMs / 1_000));

    warningTimerRef.current = setTimeout(() => {
      showWarningRef.current = true;
      setShowWarning(true);
      setSecondsLeft(warningSeconds);
      countdownRef.current = setInterval(() => {
        setSecondsLeft((s) => (s > 1 ? s - 1 : 0));
      }, 1_000);
    }, remaining - warningWindowMs);

    logoutTimerRef.current = setTimeout(() => triggerLogout(), remaining);
  }, [clearTimers, triggerLogout]);

  const dismissAndReschedule = useCallback(() => {
    showWarningRef.current = false;
    setShowWarning(false);
    schedule();
  }, [schedule]);

  const stayLoggedIn = useCallback(() => {
    writeLastActivity(userIdRef.current, Date.now());
    dismissAndReschedule();
  }, [dismissAndReschedule]);

  // Choosing to leave from the warning modal is as deliberate as using the
  // profile menu, so it doesn't resume anywhere on the next sign-in.
  const logoutNow = useCallback(() => {
    triggerLogout({ reason: 'user' });
  }, [triggerLogout]);

  useEffect(() => {
    if (!user || timeoutMsRef.current === null) {
      clearTimers();
      return;
    }

    if (readLastActivity(userIdRef.current) === null) {
      writeLastActivity(userIdRef.current, Date.now());
    }
    schedule();

    const handleActivity = () => {
      // While the warning is showing, only an explicit "Stay logged in" resets —
      // stray mouse movement shouldn't silently cancel a security timeout.
      if (showWarningRef.current || haltedRef.current) return;
      const now = Date.now();
      if (now - lastWriteRef.current < ACTIVITY_THROTTLE_MS) return;
      lastWriteRef.current = now;
      writeLastActivity(userIdRef.current, now);
      schedule();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === LAST_ACTIVITY_KEY) {
        // Only this user's own activity extends the session. A stamp from
        // anyone else reads as "no activity" and would arm a full fresh window,
        // silently cancelling a warning that's already counting down.
        if (parseLastActivity(event.newValue, userIdRef.current) === null) {
          return;
        }
        dismissAndReschedule();
      } else if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) {
        // This tab was signed out too, so it remembers its own page — but only
        // if the tab that broadcast was idled out rather than signing out.
        triggerLogout({
          broadcast: false,
          reason: readLogoutReason(event.newValue),
        });
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true }),
    );
    window.addEventListener('storage', handleStorage);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
      window.removeEventListener('storage', handleStorage);
      clearTimers();
    };
  }, [user, schedule, dismissAndReschedule, triggerLogout, clearTimers]);

  return { showWarning, secondsLeft, stayLoggedIn, logoutNow };
}
